import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Button,
    Input,
    List,
    Card,
    Badge,
    Empty,
    message,
    Layout,
    Typography,
    Avatar,
    Space,
    Divider,
    Tooltip,
    Select,
    Tag,
    Collapse,
    Popover
} from 'antd';
import {
    UserOutlined,
    SendOutlined,
    CloseOutlined,
    LogoutOutlined,
    TeamOutlined,
    ThunderboltOutlined,
    GlobalOutlined,
    SearchOutlined,
    DeleteOutlined,
    PushpinOutlined,
    MessageOutlined,
    ReloadOutlined,
    FilterOutlined,
    ClockCircleOutlined,
    BarChartOutlined,
    ApiOutlined,
    SmileOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import api from '../api';

const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const AdminChat = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [activeUsers, setActiveUsers] = useState({});
    const [selectedUser, setSelectedUser] = useState(null);
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
    const [isSending, setIsSending] = useState(false);
    const [isBroadcasting, setIsBroadcasting] = useState(false);
    const [manualBadges, setManualBadges] = useState(() => {
        const saved = localStorage.getItem('admin_chat_pinned');
        return saved ? new Set(JSON.parse(saved)) : new Set();
    });
    const [messagesByUser, setMessagesByUser] = useState(() => {
        const saved = localStorage.getItem('admin_chat_messages');
        return saved ? JSON.parse(saved) : {};
    });
    const [inputValue, setInputValue] = useState('');
    const [broadcastValue, setBroadcastValue] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [minAge, setMinAge] = useState('');
    const [maxAge, setMaxAge] = useState('');
    const [sortBy, setSortBy] = useState('last_interacted');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterVersion, setFilterVersion] = useState('all');
    const [minPassed, setMinPassed] = useState('');
    const [isForcedVisible, setIsForcedVisible] = useState(() => {
        return localStorage.getItem('admin_chat_force_show') === 'true';
    });

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const broadcastInputRef = useRef(null);

    // Dynamic refs to avoid effect re-runs
    const activeUsersRef = useRef(activeUsers);
    activeUsersRef.current = activeUsers;

    const selectedUserRef = useRef(selectedUser);
    selectedUserRef.current = selectedUser;

    const isForcedVisibleRef = useRef(isForcedVisible);
    isForcedVisibleRef.current = isForcedVisible;

    const manualBadgesRef = useRef(manualBadges);
    manualBadgesRef.current = manualBadges;

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const EMOJIS = ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧', '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮', '🤧', '🥵', '🥶', '😷', '🤒', '🤕', '🤑', '🤠', '😈', '👿', '👹', '👺', '🤡', '💩', '👻', '💀', '☠️', '👽', '👾', '🤖', '🎃', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '😿', '😾'];

    const ADMIN_PASSWORD = 'avtodas_admin_2026';

    const addEmoji = (emoji, isBroadcast = false) => {
        if (isBroadcast) {
            setBroadcastValue(prev => prev + emoji);
            if (broadcastInputRef.current) broadcastInputRef.current.focus();
        } else {
            setInputValue(prev => prev + emoji);
            if (inputRef.current) inputRef.current.focus();
        }
    };

    useEffect(() => {
        localStorage.setItem('admin_chat_messages', JSON.stringify(messagesByUser));
    }, [messagesByUser]);

    useEffect(() => {
        localStorage.setItem('admin_chat_pinned', JSON.stringify(Array.from(manualBadges)));
    }, [manualBadges]);

    useEffect(() => {
        localStorage.setItem('admin_chat_force_show', isForcedVisible);
    }, [isForcedVisible]);

    const syncAdminStatus = (forceShow, pins) => {
        api.API.post(api.Chat + '/heartbeat', { 
            name: 'Admin',
            force_show: forceShow !== undefined ? forceShow : isForcedVisibleRef.current,
            pinned_users: Array.from(pins !== undefined ? pins : manualBadgesRef.current)
        }, {
            headers: { 'X-Chat-Session-Id': 'admin' }
        }).catch(() => {});
    };

    // Main Polling Effect - ONLY depends on isAuthenticated! Zero infinite loops!
    useEffect(() => {
        if (!isAuthenticated) return;

        let isSubscribed = true;

        const fetchUsers = () => {
            api.API.get(api.Chat + '/active-users').then(response => {
                if (!isSubscribed || !response) return;
                const users = response?.data?.users || response?.users || (typeof response === 'object' && !Array.isArray(response) ? response : null);
                if (users) {
                    setActiveUsers(users);
                }
            }).catch(() => {});
        };

        const fetchMessages = () => {
            api.API.get(api.Chat + '/poll', {
                headers: { 'X-Chat-Session-Id': 'admin' }
            }).then(response => {
                if (!isSubscribed || !response) return;
                const msgs = response?.data?.messages || response?.messages;
                if (msgs && msgs.length > 0) {
                    setMessagesByUser(prev => {
                        const next = { ...prev };
                        msgs.forEach(msg => {
                            if (!next[msg.from]) next[msg.from] = [];
                            const exists = next[msg.from].some(m => m.id === msg.id || (m.timestamp === msg.timestamp && m.text === msg.text));
                            if (!exists) {
                                next[msg.from].push(msg);
                                if (selectedUserRef.current !== msg.from) {
                                    const senderName = activeUsersRef.current[msg.from]?.name || msg.from.substr(0, 6);
                                    message.info(`Նոր հաղորդագրություն ${senderName}-ից`);
                                }
                            }
                        });
                        return next;
                    });
                }
            }).catch(() => {});
        };

        // Initial calls
        fetchUsers();
        fetchMessages();
        syncAdminStatus();

        // Stable intervals
        const messagePollInterval = setInterval(fetchMessages, 3500);
        const userPollInterval = setInterval(fetchUsers, 5000);
        const heartbeatInterval = setInterval(syncAdminStatus, 10000);

        return () => {
            isSubscribed = false;
            clearInterval(messagePollInterval);
            clearInterval(userPollInterval);
            clearInterval(heartbeatInterval);
        };
    }, [isAuthenticated]);

    // Fetch history for selected user
    useEffect(() => {
        if (!selectedUser || !isAuthenticated) return;
        
        let isSubscribed = true;
        api.API.get(api.Chat + '/history/' + selectedUser).then(response => {
            if (!isSubscribed || !response) return;
            const list = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
            if (list.length > 0) {
                setMessagesByUser(prev => {
                    const next = { ...prev };
                    const existingIds = new Set((next[selectedUser] || []).map(m => m.id || (m.timestamp + m.text)));
                    const newMsgs = list.map(m => ({
                        id: m.id,
                        from: m.from,
                        text: m.text,
                        timestamp: m.timestamp,
                        is_broadcast: !!m.is_broadcast
                    })).filter(m => !existingIds.has(m.id || (m.timestamp + m.text)));
                    
                    if (newMsgs.length > 0) {
                        next[selectedUser] = [...(next[selectedUser] || []), ...newMsgs]
                            .sort((a, b) => a.timestamp - b.timestamp);
                    }
                    return next;
                });
            }
        }).catch(() => {});

        return () => {
            isSubscribed = false;
        };
    }, [selectedUser, isAuthenticated]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messagesByUser, selectedUser]);

    const toggleManualBadge = (id) => {
        setManualBadges(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            syncAdminStatus(isForcedVisible, next);
            return next;
        });
    };

    const toggleGlobalVisibility = () => {
        const nextValue = !isForcedVisible;
        setIsForcedVisible(nextValue);
        syncAdminStatus(nextValue, manualBadges);
    };

    const handleLogin = () => {
        if (password === ADMIN_PASSWORD) {
            setIsAuthenticated(true);
            setManualBadges(new Set());
            message.success('Մուտքը հաջողվեց');
        } else {
            message.error('Սխալ գաղտնաբառ');
        }
    };

    const handleSend = () => {
        if (!selectedUser || !inputValue.trim() || isSending) return;
        const text = inputValue.trim();
        setIsSending(true);

        api.API.post(api.Chat + '/send', {
            to_session_id: selectedUser,
            text: text
        }, {
            headers: { 'X-Chat-Session-Id': 'admin' }
        }).then((res) => {
            setInputValue('');
            const resMsg = res?.data?.message || res?.message;
            const now = Math.floor(Date.now() / 1000);
            const myMsg = { 
                id: resMsg?.id || Math.random().toString(36).substr(2, 9), 
                from: 'admin', 
                text: text, 
                timestamp: resMsg?.timestamp || now 
            };
            setMessagesByUser(prev => {
                const next = { ...prev };
                if (!next[selectedUser]) next[selectedUser] = [];
                if (!next[selectedUser].some(m => m.id === myMsg.id)) {
                    next[selectedUser] = [...next[selectedUser], myMsg];
                }
                return next;
            });
            setActiveUsers(prev => {
                if (!prev[selectedUser]) return prev;
                return {
                    ...prev,
                    [selectedUser]: {
                        ...prev[selectedUser],
                        last_message: text,
                        last_message_from: 'admin',
                        last_message_at: myMsg.timestamp
                    }
                };
            });
        }).catch(err => {
            console.error('Send error:', err);
            message.error('Չհաջողվեց ուղարկել հաղորդագրությունը');
        }).finally(() => {
            setIsSending(false);
            if (inputRef.current) inputRef.current.focus();
        });
    };

    const handleBroadcast = () => {
        if (!broadcastValue.trim() || isBroadcasting) return;
        const text = broadcastValue.trim();
        setIsBroadcasting(true);

        api.API.post(api.Chat + '/broadcast', { text: text }, {
            headers: { 'X-Chat-Session-Id': 'admin' }
        }).then(res => {
            setBroadcastValue('');
            const count = res?.data?.count !== undefined ? res?.data?.count : (res?.count !== undefined ? res?.count : 0);
            message.success(`Հաղորդագրությունը ուղարկվեց ${count} օգտատիրոջ`);
            const now = Math.floor(Date.now() / 1000);
            const myMsg = { 
                id: 'bcast_' + Math.random().toString(36).substr(2, 9),
                from: 'admin', 
                text: text, 
                is_broadcast: true, 
                timestamp: now 
            };
            setMessagesByUser(prev => {
                const next = { ...prev };
                Object.keys(activeUsers).forEach(uid => {
                    if (uid === 'admin') return;
                    if (!next[uid]) next[uid] = [];
                    next[uid].push(myMsg);
                });
                return next;
            });
        }).catch(err => {
            console.error('Broadcast error:', err);
            message.error('Չհաջողվեց ուղարկել համընդհանուր հաղորդագրությունը');
        }).finally(() => {
            setIsBroadcasting(false);
        });
    };

    const handleClearHistory = () => {
        api.API.post(api.Chat + '/clear-all').then(() => {
            setMessagesByUser({});
            localStorage.removeItem('admin_chat_messages');
            message.success('Չատի պատմությունը մաքրվեց');
        }).catch(() => {});
    };

    const handleCloseChat = (uId) => {
        api.API.post(api.Chat + '/close', { target_session_id: uId }, {
            headers: { 'X-Chat-Session-Id': 'admin' }
        }).then(() => {
            setMessagesByUser(prev => {
                const next = { ...prev };
                delete next[uId];
                return next;
            });
            if (selectedUser === uId) {
                setSelectedUser(null);
            }
            message.success('Չատը ավարտվեց և մաքրվեց');
        }).catch(() => {});
    };

    const handleUpdateApp = (uId) => {
        api.API.post(api.Chat + '/trigger-update', { target_session_id: uId }, {
            headers: { 'X-Chat-Session-Id': 'admin' }
        }).then(() => {
            message.success('Թարմացման հրահանգը ուղարկվեց');
        }).catch(() => {});
    };

    const handleUpdateAll = () => {
        api.API.post(api.Chat + '/trigger-update', {}, {
            headers: { 'X-Chat-Session-Id': 'admin' }
        }).then(() => {
            message.success('Թարմացման հրահանգը ուղարկվեց բոլորին');
        }).catch(() => {});
    };

    const filteredAndSortedUserIds = useMemo(() => {
        const allUserIds = Array.from(new Set([
            ...Object.keys(activeUsers),
            ...Object.keys(messagesByUser)
        ])).filter(id => id !== 'admin');

        return allUserIds
            .filter(id => {
                const user = activeUsers[id] || {};
                
                // Name/ID/Message/IP Match
                const query = searchQuery.toLowerCase();
                const nameMatch = (user.name || '').toLowerCase().includes(query) || 
                                 id.toLowerCase().includes(query) ||
                                 (user.last_message || '').toLowerCase().includes(query) ||
                                 (user.ip || '').toLowerCase().includes(query);
                
                // Age Match
                const age = parseInt(user.age);
                const minA = parseInt(minAge);
                const maxA = parseInt(maxAge);
                let ageMatch = true;
                if (!isNaN(minA) && (isNaN(age) || age < minA)) ageMatch = false;
                if (!isNaN(maxA) && (isNaN(age) || age > maxA)) ageMatch = false;

                // Status Match
                let statusMatch = true;
                if (filterStatus !== 'all') {
                    if (filterStatus === 'online' && user.status !== 'active') statusMatch = false;
                    if (filterStatus === 'offline' && user.status !== 'offline') statusMatch = false;
                    if (filterStatus === 'closed' && user.status !== 'closed') statusMatch = false;
                }

                // Version Match
                let versionMatch = true;
                if (filterVersion !== 'all' && user.app_version !== filterVersion) versionMatch = false;

                // Progress Match
                let progressMatch = true;
                const minP = parseInt(minPassed);
                if (!isNaN(minP) && (!user.progress || user.progress.passed < minP)) progressMatch = false;

                return nameMatch && ageMatch && statusMatch && versionMatch && progressMatch;
            })
            .sort((a, b) => {
                // Pin always comes first
                const isPinnedA = manualBadges.has(a);
                const isPinnedB = manualBadges.has(b);
                if (isPinnedA !== isPinnedB) return isPinnedA ? -1 : 1;

                const userA = activeUsers[a] || {};
                const userB = activeUsers[b] || {};
                const msgsA = messagesByUser[a] || [];
                const msgsB = messagesByUser[b] || [];

                switch (sortBy) {
                    case 'last_interacted':
                        const getUserReplyTime = (u, msgs) => {
                            const localUserMsg = [...(msgs || [])].reverse().find(m => m.from !== 'admin')?.timestamp || 0;
                            const serverUserMsg = u.last_user_message_at || (u.last_message_from && u.last_message_from !== 'admin' ? u.last_message_at : 0) || 0;
                            return Math.max(localUserMsg, serverUserMsg);
                        };

                        const getAnyChatTime = (u, msgs) => {
                            const localAny = (msgs && msgs.length > 0) ? (msgs[msgs.length - 1]?.timestamp || 0) : 0;
                            const serverAny = u.last_message_at || 0;
                            return Math.max(localAny, serverAny);
                        };

                        const replyA = getUserReplyTime(userA, msgsA);
                        const replyB = getUserReplyTime(userB, msgsB);

                        if (replyA > 0 || replyB > 0) {
                            if (replyA !== replyB) return replyB - replyA;
                        }

                        const chatA = getAnyChatTime(userA, msgsA);
                        const chatB = getAnyChatTime(userB, msgsB);

                        if (chatA > 0 || chatB > 0) {
                            if (chatA !== chatB) return chatB - chatA;
                        }

                        return (userB.last_seen || 0) - (userA.last_seen || 0);

                    case 'last_activity':
                        const lastA = Math.max(
                            userA.last_message_at || 0,
                            userA.last_seen || 0,
                            [...(msgsA || [])].reverse().find(m => m.from !== 'admin')?.timestamp || 0
                        );
                        const lastB = Math.max(
                            userB.last_message_at || 0,
                            userB.last_seen || 0,
                            [...(msgsB || [])].reverse().find(m => m.from !== 'admin')?.timestamp || 0
                        );
                        return lastB - lastA;

                    case 'progress':
                        const progA = userA.progress?.passed || 0;
                        const progB = userB.progress?.passed || 0;
                        if (progA !== progB) return progB - progA;
                        return (userA.progress?.true || 0) - (userB.progress?.true || 0);

                    case 'version':
                        return (userB.app_version || '').localeCompare(userA.app_version || '');

                    case 'name':
                        return (userA.name || 'Z').localeCompare(userB.name || 'Z');

                    case 'age':
                        return (parseInt(userB.age) || 0) - (parseInt(userA.age) || 0);

                    default:
                        return b.localeCompare(a);
                }
            });
    }, [activeUsers, searchQuery, minAge, maxAge, messagesByUser, sortBy, filterStatus, filterVersion, minPassed, manualBadges]);

    if (!isAuthenticated) {
        return (
            <div style={{ minHeight: isMobile ? '65vh' : '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f0f2f5', padding: isMobile ? 12 : 0 }}>
                <Card title={<Title level={4} style={{ margin: 0 }}>Ադմինիստրատորի Մուտք</Title>} style={{ width: isMobile ? '100%' : 400, maxWidth: 400, textAlign: 'center', borderRadius: 16, boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}>
                    <ThunderboltOutlined style={{ fontSize: 44, color: '#1890ff', marginBottom: 16 }} />
                    <Input.Password 
                        placeholder="Գաղտնաբառ" 
                        value={password} 
                        onChange={e => setPassword(e.target.value)}
                        onPressEnter={handleLogin}
                        style={{ marginBottom: 16, borderRadius: 8 }}
                        size="large"
                    />
                    <Button type="primary" block onClick={handleLogin} size="large" style={{ borderRadius: 8 }}>Մուտք</Button>
                </Card>
            </div>
        );
    }

    return (
        <Layout style={{ 
            height: isMobile ? 'calc(100vh - 85px)' : '88vh', 
            minHeight: isMobile ? '450px' : '550px',
            background: '#fff', 
            borderRadius: isMobile ? 12 : 20, 
            overflow: 'hidden', 
            boxShadow: '0 10px 40px rgba(0,0,0,0.06)',
            margin: isMobile ? '-8px -8px 0 -8px' : 0
        }}>
            {(!isMobile || !selectedUser) && (
                <Sider 
                    width={isMobile ? '100%' : 350} 
                    theme="light" 
                    className="admin-sider" 
                    style={{ 
                        borderRight: isMobile ? 'none' : '1px solid #f0f0f0', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        height: '100%',
                        maxWidth: isMobile ? '100%' : 350,
                        flex: isMobile ? '1 1 100%' : '0 0 350px'
                    }}
                >
                    <div style={{ padding: isMobile ? '12px 14px' : '20px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <Space align="center" size="small">
                                <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>Չատեր</Title>
                                <Badge count={filteredAndSortedUserIds.length} overflowCount={999} style={{ backgroundColor: '#1890ff' }} />
                                <Tooltip title={isForcedVisible ? "Թաքցնել չատը բոլորից" : "Ցուցադրել չատը բոլորին"}>
                                    <Button 
                                        type={isForcedVisible ? "primary" : "default"} 
                                        shape="circle" 
                                        icon={<MessageOutlined />} 
                                        size="small"
                                        onClick={toggleGlobalVisibility}
                                    />
                                </Tooltip>
                            </Space>
                                
                            <Space size={2}>
                                <Tooltip title="Թարմացնել բոլորի հավելվածը">
                                    <Button type="text" icon={<ReloadOutlined />} onClick={handleUpdateAll} size="small" />
                                </Tooltip>
                                <Tooltip title="Մաքրել պատմությունը">
                                    <Button type="text" icon={<DeleteOutlined />} onClick={handleClearHistory} size="small" />
                                </Tooltip>
                                <Tooltip title="Դուրս գալ">
                                    <Button type="text" danger icon={<LogoutOutlined />} onClick={() => setIsAuthenticated(false)} size="small" />
                                </Tooltip>
                            </Space>
                        </div>

                        <Space direction="vertical" style={{ width: '100%' }} size="small">
                            <Input 
                                placeholder="Փնտրել անուն կամ ID..." 
                                prefix={<SearchOutlined />} 
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                style={{ borderRadius: 8 }}
                                allowClear
                                size="middle"
                            />
                            
                            <Collapse ghost size="small" expandIconPosition="end" style={{ background: '#f8f9fa', borderRadius: 8, marginTop: 2 }}>
                                <Collapse.Panel header={<Space><FilterOutlined style={{ fontSize: 12 }} /> <Text style={{ fontSize: 12 }}>Ֆիլտրեր և Սորտավորում</Text></Space>} key="1">
                                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                                        <div>
                                            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Սորտավորել ըստ՝</Text>
                                            <Select 
                                                size="small"
                                                style={{ width: '100%' }} 
                                                value={sortBy} 
                                                onChange={setSortBy}
                                            >
                                                <Option value="last_interacted"><MessageOutlined /> Վերջին պատասխանած / Շփված</Option>
                                                <Option value="last_activity"><ClockCircleOutlined /> Վերջին ակտիվություն</Option>
                                                <Option value="progress"><BarChartOutlined /> Առաջընթաց</Option>
                                                <Option value="version"><ApiOutlined /> Հավելվածի տարբերակ</Option>
                                                <Option value="name"><UserOutlined /> Անուն</Option>
                                                <Option value="age">Տարիք</Option>
                                            </Select>
                                        </div>

                                        <Divider style={{ margin: '4px 0' }} />

                                        <div>
                                            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Կարգավիճակ</Text>
                                            <Select 
                                                size="small"
                                                style={{ width: '100%' }} 
                                                value={filterStatus} 
                                                onChange={setFilterStatus}
                                            >
                                                <Option value="all">Բոլորը</Option>
                                                <Option value="online">Միայն Օնլայն</Option>
                                                <Option value="offline">Օֆլայն</Option>
                                                <Option value="closed">Փակված</Option>
                                            </Select>
                                        </div>

                                        <div style={{ display: 'flex', gap: 4 }}>
                                            <div style={{ flex: 1 }}>
                                                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Տարիք</Text>
                                                <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                                    <Input 
                                                        size="small"
                                                        type="number"
                                                        placeholder="Min" 
                                                        value={minAge}
                                                        onChange={e => setMinAge(e.target.value)}
                                                    />
                                                    <Input 
                                                        size="small"
                                                        type="number"
                                                        placeholder="Max" 
                                                        value={maxAge}
                                                        onChange={e => setMaxAge(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Թեստեր (Min)</Text>
                                                <Input 
                                                    size="small"
                                                    type="number"
                                                    placeholder="Min" 
                                                    value={minPassed}
                                                    onChange={e => setMinPassed(e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Հավելվածի տարբերակ</Text>
                                            <Select 
                                                size="small"
                                                style={{ width: '100%' }} 
                                                value={filterVersion} 
                                                onChange={setFilterVersion}
                                            >
                                                <Option value="all">Բոլորը</Option>
                                                {Array.from(new Set(Object.values(activeUsers).map(u => u.app_version).filter(Boolean))).map(v => (
                                                    <Option key={v} value={v}>{v}</Option>
                                                ))}
                                            </Select>
                                        </div>
                                        
                                        <Button 
                                            size="small" 
                                            type="link" 
                                            style={{ fontSize: 11, padding: 0 }} 
                                            onClick={() => {
                                                setSearchQuery('');
                                                setMinAge('');
                                                setMaxAge('');
                                                setSortBy('last_interacted');
                                                setFilterStatus('all');
                                                setFilterVersion('all');
                                                setMinPassed('');
                                            }}
                                        >
                                            Մաքրել ֆիլտրերը
                                        </Button>
                                    </Space>
                                </Collapse.Panel>
                            </Collapse>
                        </Space>
                        
                        <Collapse 
                            ghost 
                            size="small" 
                            defaultActiveKey={isMobile ? [] : ['broadcast']}
                            style={{ background: '#f9f9f9', borderRadius: 10, border: '1px dashed #d9d9d9', marginTop: 8 }}
                        >
                            <Collapse.Panel 
                                header={
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <GlobalOutlined style={{ color: '#1890ff' }} />
                                        <Text strong style={{ fontSize: 12 }}>Հայտարարություն բոլորին</Text>
                                    </div>
                                } 
                                key="broadcast"
                                extra={
                                    <Popover
                                        content={
                                            <div style={{ width: 250, height: 150, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 4, padding: 4 }}>
                                                {EMOJIS.slice(0, 50).map(emoji => (
                                                    <span 
                                                        key={emoji} 
                                                        style={{ fontSize: 18, cursor: 'pointer', userSelect: 'none' }}
                                                        onClick={(e) => { e.stopPropagation(); addEmoji(emoji, true); }}
                                                    >
                                                        {emoji}
                                                    </span>
                                                ))}
                                            </div>
                                        }
                                        title="Էմոջի"
                                        trigger="click"
                                        placement="right"
                                    >
                                        <Button size="small" type="text" icon={<SmileOutlined style={{ color: '#1890ff' }} />} onClick={e => e.stopPropagation()} />
                                    </Popover>
                                }
                            >
                                <Input.TextArea 
                                    ref={broadcastInputRef}
                                    placeholder="Գրեք բոլորին..." 
                                    rows={2} 
                                    value={broadcastValue}
                                    onChange={e => setBroadcastValue(e.target.value)}
                                    style={{ marginBottom: 8, borderRadius: 8, fontSize: 12 }}
                                    disabled={isBroadcasting}
                                />
                                <Button 
                                    type="primary" 
                                    block 
                                    size="small" 
                                    icon={<TeamOutlined />} 
                                    onClick={handleBroadcast}
                                    loading={isBroadcasting}
                                    disabled={!broadcastValue.trim() || isBroadcasting}
                                    style={{ borderRadius: 6 }}
                                >
                                    Ուղարկել
                                </Button>
                            </Collapse.Panel>
                        </Collapse>
                    </div>

                    <div className="user-list-container" style={{ flex: 1, overflowY: 'auto', paddingBottom: 20 }}>
                        <List
                            itemLayout="horizontal"
                            dataSource={filteredAndSortedUserIds}
                            renderItem={id => {
                                const user = activeUsers[id] || { name: 'Անհայտ օգտատեր', status: 'offline' };
                                const isOffline = user.status === 'offline' || user.status === 'closed';
                                const badgeColor = user.status === 'active' ? '#52c41a' : '#ff4d4f';
                                const isFilled = user.name && user.name !== 'Անանուն' && !user.name.startsWith('Guest') && user.name !== 'Անհայտ օգտատեր';
                                
                                return (
                                    <List.Item 
                                        onClick={() => setSelectedUser(id)}
                                        style={{ 
                                            cursor: 'pointer', 
                                            padding: isMobile ? '10px 14px' : '12px 20px',
                                            background: selectedUser === id ? '#e6f7ff' : isFilled ? '#f9f9f9' : 'transparent',
                                            borderLeft: selectedUser === id ? '4px solid #1890ff' : '4px solid transparent',
                                            transition: 'all 0.3s',
                                            opacity: isOffline ? 0.6 : 1
                                        }}
                                        actions={[
                                            <Tooltip title="Ջնջել պատմությունը">
                                                <Button 
                                                    type="text" 
                                                    danger 
                                                    icon={<DeleteOutlined />} 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCloseChat(id);
                                                    }}
                                                    size="small"
                                                />
                                            </Tooltip>
                                        ]}
                                    >
                                        <List.Item.Meta
                                            avatar={
                                                <Badge count={isOffline ? '!' : 0} offset={[-2, 32]} color="#ff4d4f">
                                                    <Badge dot={manualBadges.has(id) || (user.last_message_from && user.last_message_from !== 'admin') || (messagesByUser[id]?.length > 0 && messagesByUser[id][messagesByUser[id].length - 1].from !== 'admin')} color="#1890ff" offset={[-2, 5]}>
                                                        <Avatar src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${id}`} style={{ border: `2px solid ${badgeColor}` }} />
                                                    </Badge>
                                                </Badge>
                                            }
                                            title={
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Text strong={selectedUser === id || isFilled} delete={user.status === 'closed'} style={{ fontSize: isMobile ? 13 : 14 }}>
                                                            {user.name || id.substr(0, 8)} {user.age && user.age !== 'N/A' && user.age !== 0 ? `(${user.age} տ․)` : ''}
                                                        </Text>
                                                        {user.last_message_at ? (
                                                            <Text type="secondary" style={{ fontSize: 9 }}>
                                                                {new Date(user.last_message_at * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                            </Text>
                                                        ) : null}
                                                    </div>
                                                    <Text type="secondary" style={{ fontSize: 10 }}>
                                                        {user.status === 'active' ? '🟢 Օնլայն' : user.status === 'closed' ? '⚪ Փակված' : '🔴 Անհասանելի'}
                                                    </Text>
                                                </div>
                                            }
                                            description={
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    {user.last_message && (
                                                        <div style={{
                                                            fontSize: 11,
                                                            color: '#262626',
                                                            background: '#f0f2f5',
                                                            padding: '3px 8px',
                                                            borderRadius: 6,
                                                            marginTop: 2,
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}>
                                                            <span style={{ fontWeight: 600, color: user.last_message_from === 'admin' ? '#1890ff' : '#52c41a' }}>
                                                                {user.last_message_from === 'admin' ? 'Դուք: ' : ''}
                                                            </span>
                                                            {user.last_message}
                                                        </div>
                                                    )}
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                                                        <Text type="secondary" style={{ fontSize: 10 }}>ID: {id.substr(0, 8)}...</Text>
                                                        <Space size={4}>
                                                            {user.app_version && <Tag size="small" style={{ fontSize: 8, margin: 0 }}>v{user.app_version}</Tag>}
                                                            {user.ip && <Text type="secondary" style={{ fontSize: 10 }}>{user.ip}</Text>}
                                                        </Space>
                                                    </div>
                                                    {user.progress && (
                                                        <div style={{ fontSize: 10, color: '#8c8c8c' }}>
                                                            <Space split={<Divider type="vertical" style={{ margin: 0 }} />}>
                                                                <span>✅ {Math.max(0, user.progress.true || 0)}</span>
                                                                <span>❌ {Math.max(0, user.progress.wrong || 0)}</span>
                                                                <span>📊 {user.progress.passed || 0}/60</span>
                                                            </Space>
                                                        </div>
                                                    )}
                                                    {user.current_page && (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Text type="secondary" style={{ fontSize: 9, fontStyle: 'italic' }}>
                                                                📍 {user.current_page}
                                                            </Text>
                                                            {user.push_time && (
                                                                <Text type="secondary" style={{ fontSize: 8 }}>
                                                                    🚀 {user.push_time}
                                                                </Text>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            }
                                        />
                                    </List.Item>
                                );
                            }}
                            locale={{ emptyText: <div style={{ padding: 40 }}><Empty description="Օգտատերեր չկան" /></div> }}
                        />
                    </div>
                </Sider>
            )}
            
            {(!isMobile || selectedUser) && (
                <Layout style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {selectedUser ? (
                        <>
                            <Header style={{ 
                                background: '#fff', 
                                padding: isMobile ? '8px 12px' : '0 25px', 
                                borderBottom: '1px solid #f0f0f0', 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center', 
                                height: 'auto', 
                                minHeight: isMobile ? 60 : 76, 
                                paddingBlock: isMobile ? 8 : 10,
                                flexShrink: 0
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, overflow: 'hidden' }}>
                                    {isMobile && (
                                        <Button 
                                            type="text" 
                                            icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />} 
                                            onClick={() => setSelectedUser(null)} 
                                            style={{ padding: '4px 6px', marginRight: 2 }} 
                                        />
                                    )}
                                    <Badge dot color={activeUsers[selectedUser]?.status === 'active' ? '#52c41a' : '#ff4d4f'}>
                                        <Avatar size={isMobile ? "default" : "large"} src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${selectedUser}`} />
                                    </Badge>
                                    <div style={{ overflow: 'hidden' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                            <Title level={isMobile ? 5 : 4} style={{ margin: 0, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: isMobile ? 120 : 250 }}>
                                                {activeUsers[selectedUser]?.name || selectedUser} 
                                            </Title>
                                            {activeUsers[selectedUser]?.age && activeUsers[selectedUser]?.age !== 'N/A' && activeUsers[selectedUser]?.age !== 0 ? (
                                                <Text type="secondary" style={{ fontSize: 11 }}>({activeUsers[selectedUser]?.age}տ)</Text>
                                            ) : null}
                                            <Tag color={activeUsers[selectedUser]?.status === 'active' ? "success" : "error"} style={{ borderRadius: 10, margin: 0, fontSize: 10, padding: '0 6px' }}>
                                                {activeUsers[selectedUser]?.status === 'active' ? 'Օնլայն' : 'Անհասանելի'}
                                            </Tag>
                                        </div>
                                        {!isMobile ? (
                                            <>
                                                <Space size="middle" style={{ marginTop: 4 }}>
                                                    {activeUsers[selectedUser]?.app_version && (
                                                        <Space size={4}>
                                                            <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>v{activeUsers[selectedUser].app_version}</Tag>
                                                            {activeUsers[selectedUser].push_time && (
                                                                <Text type="secondary" style={{ fontSize: 11 }}>
                                                                    (Push: {activeUsers[selectedUser].push_time})
                                                                </Text>
                                                            )}
                                                        </Space>
                                                    )}
                                                    {activeUsers[selectedUser]?.ip && (
                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                            <GlobalOutlined /> {activeUsers[selectedUser].ip}
                                                        </Text>
                                                    )}
                                                    {activeUsers[selectedUser]?.current_page && (
                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                            📍 {activeUsers[selectedUser].current_page}
                                                        </Text>
                                                    )}
                                                </Space>
                                                {activeUsers[selectedUser]?.progress && (
                                                    <div style={{ marginTop: 4 }}>
                                                        <Space split={<Divider type="vertical" />} style={{ fontSize: 12, color: '#595959' }}>
                                                            <Text type="success">Ճիշտ: {Math.max(0, activeUsers[selectedUser].progress.true || 0)}</Text>
                                                            <Text type="danger">Սխալ: {Math.max(0, activeUsers[selectedUser].progress.wrong || 0)}</Text>
                                                            <Text type="warning">Թեստեր: {activeUsers[selectedUser].progress.passed || 0}/60</Text>
                                                            <Text strong style={{ color: '#1890ff' }}>
                                                                Ճշտություն: {activeUsers[selectedUser].progress.passed > 0 && activeUsers[selectedUser].progress.true > 0 ? Math.min(100, Math.round((activeUsers[selectedUser].progress.true / (activeUsers[selectedUser].progress.passed * 20)) * 100)) : 0}%
                                                            </Text>
                                                        </Space>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div style={{ display: 'flex', gap: 6, fontSize: 10, color: '#8c8c8c', marginTop: 2 }}>
                                                {activeUsers[selectedUser]?.app_version && <Tag size="small" style={{ fontSize: 9, margin: 0, padding: '0 4px' }}>v{activeUsers[selectedUser].app_version}</Tag>}
                                                {activeUsers[selectedUser]?.progress && (
                                                    <span>✅{Math.max(0, activeUsers[selectedUser].progress.true || 0)} ❌{Math.max(0, activeUsers[selectedUser].progress.wrong || 0)} 📊{activeUsers[selectedUser].progress.passed || 0}/60</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <Space size={isMobile ? 2 : 8} style={{ flexShrink: 0 }}>
                                    <Tooltip title="Թարմացնել օգտատիրոջ հավելվածը (մաքրել քեշը)">
                                        <Button 
                                            icon={<ReloadOutlined />} 
                                            onClick={() => handleUpdateApp(selectedUser)}
                                            size={isMobile ? "small" : "middle"}
                                        >
                                            {!isMobile && "Թարմացնել"}
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title={manualBadges.has(selectedUser) ? "Թաքցնել չատը օգտատիրոջից" : "Ցուցադրել չատը օգտատիրոջը"}>
                                        <Button 
                                            type={manualBadges.has(selectedUser) ? "primary" : "default"} 
                                            icon={<PushpinOutlined />} 
                                            onClick={() => toggleManualBadge(selectedUser)}
                                            size={isMobile ? "small" : "middle"}
                                        >
                                            {!isMobile && (manualBadges.has(selectedUser) ? "Չատը միացված է" : "Ցուցադրել չատը")}
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Ավարտել Չատը">
                                        <Button 
                                            danger 
                                            type="text" 
                                            icon={<CloseOutlined />} 
                                            onClick={() => handleCloseChat(selectedUser)}
                                            size={isMobile ? "small" : "middle"}
                                        >
                                            {!isMobile && "Ավարտել Չատը"}
                                        </Button>
                                    </Tooltip>
                                </Space>
                            </Header>
                            <Content style={{ padding: isMobile ? '12px 10px' : '25px', display: 'flex', flexDirection: 'column', background: '#f5f7f9', flex: 1, overflow: 'hidden' }}>
                                <div className="message-list-container" style={{ flex: 1, overflowY: 'auto', marginBottom: isMobile ? 10 : 20, paddingRight: isMobile ? 4 : 10 }}>
                                    {messagesByUser[selectedUser]?.map((item, idx) => {
                                        const isMe = item.from === 'admin';
                                        const time = item.timestamp ? new Date(item.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                                        return (
                                            <div key={idx} style={{ 
                                                display: 'flex', 
                                                flexDirection: 'column',
                                                alignItems: isMe ? 'flex-end' : 'flex-start',
                                                marginBottom: isMobile ? 8 : 12
                                            }}>
                                                <div style={{ 
                                                    maxWidth: isMobile ? '90%' : '85%', 
                                                    padding: isMobile ? '8px 12px' : '10px 16px', 
                                                    borderRadius: 16, 
                                                    borderTopRightRadius: isMe ? 2 : 16, 
                                                    borderTopLeftRadius: isMe ? 16 : 2,
                                                    background: isMe ? '#1890ff' : '#fff',
                                                    color: isMe ? '#fff' : '#333',
                                                    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                                                    fontSize: isMobile ? '13px' : '14px',
                                                    wordBreak: 'break-word',
                                                    position: 'relative'
                                                }}>
                                                    {item.text}
                                                    <div style={{ 
                                                        fontSize: '9px', 
                                                        marginTop: 3, 
                                                        opacity: 0.6, 
                                                        textAlign: 'right',
                                                        display: 'flex',
                                                        justifyContent: 'flex-end',
                                                        alignItems: 'center',
                                                        gap: 4
                                                    }}>
                                                        {item.is_broadcast && isMe && <span style={{ fontStyle: 'italic' }}>Համընդհանուր • </span>}
                                                        {time}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div ref={messagesEndRef} />
                                </div>
                                <div style={{ 
                                    padding: isMobile ? '8px 10px' : '15px', 
                                    background: '#fff', 
                                    borderRadius: isMobile ? 12 : 16, 
                                    boxShadow: '0 4px 15px rgba(0,0,0,0.05)', 
                                    display: 'flex', 
                                    gap: isMobile ? 8 : 12, 
                                    alignItems: 'center',
                                    flexShrink: 0
                                }}>
                                    <Popover
                                        content={
                                            <div style={{ width: isMobile ? 260 : 300, height: 180, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 6, padding: 6 }}>
                                                {EMOJIS.map(emoji => (
                                                    <span 
                                                        key={emoji} 
                                                        style={{ fontSize: 20, cursor: 'pointer', userSelect: 'none' }}
                                                        onClick={() => addEmoji(emoji)}
                                                    >
                                                        {emoji}
                                                    </span>
                                                ))}
                                            </div>
                                        }
                                        title="Ընտրեք էմոջի"
                                        trigger="click"
                                        placement="topLeft"
                                    >
                                        <Button 
                                            type="text" 
                                            icon={<SmileOutlined style={{ fontSize: isMobile ? 18 : 20, color: '#1890ff' }} />} 
                                            style={{ height: isMobile ? 36 : 40, width: isMobile ? 36 : 40, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                                        />
                                    </Popover>
                                    <Input 
                                        ref={inputRef}
                                        size={isMobile ? "middle" : "large"}
                                        placeholder="Գրեք ձեր պատասխանը..." 
                                        value={inputValue} 
                                        onChange={e => setInputValue(e.target.value)}
                                        onPressEnter={handleSend}
                                        style={{ borderRadius: 12, border: 'none', background: '#f0f2f5', flex: 1 }}
                                        disabled={isSending || activeUsers[selectedUser]?.status === 'closed'}
                                    />
                                    <Button 
                                        size={isMobile ? "middle" : "large"} 
                                        type="primary" 
                                        icon={<SendOutlined />} 
                                        onClick={handleSend} 
                                        loading={isSending}
                                        disabled={isSending || !inputValue.trim() || activeUsers[selectedUser]?.status === 'closed'}
                                        style={{ borderRadius: 12, width: isMobile ? 44 : 60, flexShrink: 0 }} 
                                    />
                                </div>
                            </Content>
                        </>
                    ) : (
                        <Content style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f5f7f9', height: '100%' }}>
                            <div style={{ textAlign: 'center', opacity: 0.5, padding: 20 }}>
                                <TeamOutlined style={{ fontSize: isMobile ? 48 : 64, marginBottom: 16 }} />
                                <Title level={isMobile ? 5 : 4}>Ընտրեք օգտատիրոջ չատը սկսելու համար</Title>
                                <Text>Կամ օգտագործեք վահանակը բոլորին հայտարարություն ուղարկելու համար</Text>
                            </div>
                        </Content>
                    )}
                </Layout>
            )}
            <style dangerouslySetInnerHTML={{ __html: `
                .user-list-container::-webkit-scrollbar,
                .message-list-container::-webkit-scrollbar {
                    width: 6px;
                }
                .user-list-container::-webkit-scrollbar-thumb,
                .message-list-container::-webkit-scrollbar-thumb {
                    background-color: #e8e8e8;
                    border-radius: 10px;
                }
                .user-list-container::-webkit-scrollbar-track,
                .message-list-container::-webkit-scrollbar-track {
                    background-color: transparent;
                }
                .admin-sider .ant-layout-sider-children {
                    display: flex;
                    flex-direction: column;
                }
            `}} />
        </Layout>
    );
};

export default AdminChat;
